import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Trash2, Send, BellRing, Users, Store, MessageCircle, Mail, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';

// Dashboard > Otomasi: registration switches + automatic reminders (tenants & sales team).

const fmt = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return isNaN(d) ? '-' : d.toLocaleString('id-ID', { timeZone: 'Asia/Jakarta', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const errOf = (e) => (e && e.response && e.response.data && e.response.data.detail) || (e && e.message) || 'Gagal';
const NEEDS = { a1: 'Form A1 & PKS', assets: 'Aset foto' };
const input = 'w-full h-11 px-3 rounded-xl bg-[#0A0707] border border-white/10 text-sm text-white focus:outline-none';

// Small badge used on tenant cards: shows the system already sent an auto reminder.
export function AutoReminderBadge({ r }) {
  if (!r) return null;
  const ok = r.ok !== false && (r.channels || []).length > 0;
  return (
    <div data-testid="auto-reminder-badge" title={(r.needs || []).map((n) => NEEDS[n] || n).join(', ')}
      className={`mt-1.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${ok ? 'border-sky-500/30 bg-sky-500/10 text-sky-300' : 'border-red-500/30 bg-red-500/10 text-red-300'}`}>
      <BellRing className="w-3 h-3" />
      {ok ? `Auto reminder H+${r.day} · ${(r.channels || []).join(' + ')}` : `Auto reminder H+${r.day} gagal`} · {fmt(r.at)}
    </div>
  );
}

function Switch({ on, onChange, label, hint, disabled, testid }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} onClick={() => !disabled && onChange(!on)} disabled={disabled} data-testid={testid}
      className="w-full flex items-center gap-3 py-3 text-left disabled:opacity-60">
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] font-bold text-white">{label}</span>
        {hint && <span className="block text-xs text-white/60 mt-0.5">{hint}</span>}
      </span>
      <span className="relative flex-none w-12 h-7 rounded-full transition" style={{ background: on ? 'var(--tv-accent)' : 'rgba(127,127,127,0.35)' }}>
        <span className="absolute top-1 w-5 h-5 rounded-full shadow transition-all" style={{ left: on ? 26 : 4, background: '#fff' }} />
      </span>
    </button>
  );
}

function Card({ icon: Icon, title, sub, children, right }) {
  return (
    <section className="tv-card rounded-2xl p-4 md:p-5 border border-white/10">
      <div className="flex items-start gap-3 mb-2">
        <span className="w-10 h-10 rounded-xl grid place-items-center flex-none" style={{ background: 'color-mix(in srgb, var(--tv-accent) 18%, transparent)', color: 'var(--tv-accent)' }}>
          <Icon className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-[17px] font-black text-white leading-tight">{title}</h3>
          {sub && <p className="text-xs text-white/60 mt-0.5">{sub}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function Btn({ children, onClick, busy, primary, testid, disabled, wide }) {
  return (
    <button type="button" onClick={onClick} disabled={busy || disabled} data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-xl text-sm font-bold disabled:opacity-60 ${wide ? 'w-full md:w-auto min-h-[50px]' : ''} ${primary ? 'text-white' : 'border border-white/15 bg-white/5 text-white'}`}
      style={primary ? { background: 'var(--tv-accent)' } : undefined}>
      {busy && <Loader2 className="w-4 h-4 animate-spin" />}{children}
    </button>
  );
}

// Sends a sample of the real reminder to one test address only.
function SampleTest({ label, hint, value, onChange, onSend, busy, testid }) {
  return (
    <div className="mt-3 rounded-xl border border-dashed border-white/15 p-3">
      <div className="text-sm font-bold text-white">{label}</div>
      <div className="text-xs text-white/60 mb-2">{hint}</div>
      <div className="flex gap-2">
        <input className={input} type="email" placeholder="Email untuk tes" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} />
        <Btn testid={testid} onClick={onSend} busy={busy} disabled={!value}><Mail className="w-4 h-4" /> Kirim contoh</Btn>
      </div>
    </div>
  );
}

export default function AutomationTab() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [daysText, setDaysText] = useState('');
  const [token, setToken] = useState('');
  const [digest, setDigest] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [testTo, setTestTo] = useState({ email: '', wa: '' });
  // Save bar sticks to the top of the bottom menu while scrolling, and settles into place at its spot.
  const [stuck, setStuck] = useState(true);
  const sentinel = useRef(null);
  const ready = !!data;
  const barRef = useRef(null);
  useEffect(() => {
    const bar = barRef.current, el = sentinel.current;
    if (!bar || !el) return undefined;
    const scroller = bar.closest('main') || window;
    let raf = 0;
    const check = () => { raf = 0; setStuck(el.getBoundingClientRect().top - bar.getBoundingClientRect().bottom > 2); };
    const on = () => { if (!raf) raf = requestAnimationFrame(check); };
    check();
    scroller.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', on);
    return () => { scroller.removeEventListener('scroll', on); window.removeEventListener('resize', on); if (raf) cancelAnimationFrame(raf); };
  }, [ready]);

  const apply = (d) => {
    setData(d);
    setCfg({ ...d.settings, sales_team: (d.settings.sales_team || []).map((m) => ({ ...m })) });
    setDaysText((d.settings.tenant_days || []).join(', '));
  };
  const load = async () => {
    try {
      const { data: d } = await api.get('/automation');
      apply(d);
      const { data: dg } = await api.get('/automation/digest');
      setDigest(dg);
    } catch (e) { setMsg(errOf(e)); }
  };
  useEffect(() => { load(); }, []);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };
  const setReg = async (k, v) => {
    setBusy(`reg-${k}`);
    try {
      const { data: reg } = await api.put('/config/registration', { [k]: v });
      setData((d) => ({ ...d, registration: reg }));
    } catch (e) { flash(errOf(e)); } finally { setBusy(''); }
  };
  const save = async (extra = {}) => {
    setBusy('save');
    try {
      const days = daysText.split(/[^0-9]+/).filter(Boolean).map(Number);
      const payload = { ...cfg, tenant_days: days, ...extra };
      delete payload.wa_token_set; delete payload.wa_token_hint; delete payload.wa_provider;
      const { data: d } = await api.put('/automation', payload);
      apply(d);
      flash('Pengaturan tersimpan.');
    } catch (e) { flash(errOf(e)); } finally { setBusy(''); }
  };
  const run = async (job) => {
    setBusy(`run-${job}`);
    try {
      const { data: r } = await api.post('/automation/run', { job });
      const n = (r.sent || []).length;
      flash(job === 'tenant' ? `${n} reminder tenant diproses.` : `Rekap dikirim ke ${n} anggota tim.`);
      await load();
    } catch (e) { flash(errOf(e)); } finally { setBusy(''); }
  };
  const test = async (channel, kind = 'ping') => {
    setBusy(`test-${kind}-${channel}`);
    try {
      const { data: r } = await api.post('/automation/test', { channel, kind, to: testTo[channel] });
      flash(r.ok ? `Terkirim (${r.label}). Cek inbox / WhatsApp.` : `Tes gagal: ${r.error}`);
      await load();
    } catch (e) { flash(errOf(e)); } finally { setBusy(''); }
  };

  if (!data || !cfg) {
    return <div className="py-16 text-center text-white/60 inline-flex w-full justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Memuat otomasi…</div>;
  }
  const reg = data.registration || {};
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setMember = (i, k, v) => setCfg((c) => ({ ...c, sales_team: c.sales_team.map((m, j) => (j === i ? { ...m, [k]: v } : m)) }));
  const due = data.tenant_due_today || [];

  return (
    <div className="space-y-4 pb-6" data-testid="automation-tab">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${data.email_configured ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-red-500/30 bg-red-500/10 text-red-300'}`}>
          Email {data.email_configured ? 'aktif' : 'belum aktif'}
        </span>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${data.wa_configured ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-amber-500/30 bg-amber-500/10 text-amber-300'}`}>
          WhatsApp {data.wa_configured ? 'aktif' : 'belum aktif'}
        </span>
      </div>
      {msg && <div role="status" className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white">{msg}</div>}

      <Card icon={Store} title="Kontrol pendaftaran" sub="Langsung berlaku di website begitu diubah.">
        <div className="divide-y divide-white/5">
          <Switch testid="reg-creator" on={reg.creator} disabled={busy === 'reg-creator'} onChange={(v) => setReg('creator', v)} label="Form Creator / Tenant" hint="Halaman /register bagian Creator" />
          <Switch testid="reg-brand" on={reg.brand} disabled={busy === 'reg-brand'} onChange={(v) => setReg('brand', v)} label="Form Brand Booth" hint="Halaman /register bagian Brand & /corporate" />
          <Switch testid="reg-fnb" on={reg.fnb} disabled={busy === 'reg-fnb'} onChange={(v) => setReg('fnb', v)} label="Form F&B" hint="Halaman /fnb" />
          <Switch testid="reg-home" on={reg.home} disabled={busy === 'reg-home'} onChange={(v) => setReg('home', v)} label="Tombol daftar di homepage" hint="Kartu Tenant & F&B di homepage dan link daftar di /home" />
        </div>
        <p className="text-xs text-white/60 mt-2 leading-relaxed">
          {reg.creator && reg.brand ? 'Creator & Brand aktif: pendaftar memilih dulu di /register.'
            : reg.creator || reg.brand ? `Hanya ${reg.creator ? 'Creator' : 'Brand'} yang aktif: /register langsung membuka form ${reg.creator ? 'Creator' : 'Brand'} tanpa pilihan.`
              : 'Creator & Brand nonaktif: /register menampilkan info pendaftaran ditutup.'}
        </p>
      </Card>

      <Card icon={BellRing} title="Reminder otomatis tenant" sub="Untuk tenant yang sudah diundang tapi belum isi Form A1 & PKS atau belum upload aset foto.">
        <div className="divide-y divide-white/5">
          <Switch testid="auto-tenant" on={cfg.tenant_enabled} onChange={(v) => set('tenant_enabled', v)} label="Kirim reminder otomatis" hint={`Dikirim setiap hari jam ${String(cfg.send_hour).padStart(2, '0')}.00 WIB`} />
          <Switch on={cfg.tenant_assets} onChange={(v) => set('tenant_assets', v)} label="Ingatkan juga aset foto" hint="Logo, foto cover & foto produk di portal tenant" />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <label className="text-xs font-bold text-white/70">Hari ke- (sejak diundang)
            <input className={`${input} mt-1`} value={daysText} onChange={(e) => setDaysText(e.target.value)} placeholder="3, 5, 6" inputMode="numeric" />
          </label>
          <label className="text-xs font-bold text-white/70">Jam kirim (WIB)
            <select className={`${input} mt-1`} value={cfg.send_hour} onChange={(e) => set('send_hour', Number(e.target.value))}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}.00</option>)}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap gap-4 mt-3 text-sm font-semibold text-white">
          <label className="inline-flex items-center gap-2 min-h-[44px]"><input type="checkbox" checked={!!cfg.tenant_email} onChange={(e) => set('tenant_email', e.target.checked)} className="w-5 h-5" style={{ accentColor: 'var(--tv-accent)' }} /> Email</label>
          <label className="inline-flex items-center gap-2 min-h-[44px]"><input type="checkbox" checked={!!cfg.tenant_wa} onChange={(e) => set('tenant_wa', e.target.checked)} className="w-5 h-5" style={{ accentColor: 'var(--tv-accent)' }} /> WhatsApp</label>
        </div>
        <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-3">
          <div className="text-sm font-bold text-white">Jadwal hari ini: {due.length} tenant</div>
          {due.length === 0 ? <div className="text-xs text-white/60 mt-1">Tidak ada tenant yang jatuh tempo reminder hari ini.</div> : (
            <ul className="mt-2 space-y-1.5">
              {due.map((t) => (
                <li key={`${t.id}-${t.day}`} className="text-sm text-white flex flex-wrap gap-x-2">
                  <b>{t.name}</b><span className="text-white/60">H+{t.day} · {t.needs.map((n) => NEEDS[n] || n).join(', ')}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3"><Btn testid="run-tenant" onClick={() => run('tenant')} busy={busy === 'run-tenant'} disabled={!due.length}><Send className="w-4 h-4" /> Kirim reminder hari ini sekarang</Btn></div>
        </div>
        <SampleTest testid="sample-tenant" label="Tes email reminder tenant" hint="Kirim contoh email reminder (pakai data tenant asli) hanya ke email ini. Tenant tidak menerima apa-apa."
          value={testTo.email} onChange={(v) => setTestTo((t) => ({ ...t, email: v }))} onSend={() => test('email', 'tenant')} busy={busy === 'test-tenant-email'} />
      </Card>

      <Card icon={Users} title="Rekap harian tim sales" sub="Daftar yang perlu di-approach & follow up, dikirim tiap pagi ke tim internal.">
        <Switch testid="auto-sales" on={cfg.sales_enabled} onChange={(v) => set('sales_enabled', v)} label="Kirim rekap harian" hint={`Jam ${String(cfg.send_hour).padStart(2, '0')}.00 WIB via email & WhatsApp`} />
        <div className="space-y-2 mt-2">
          {cfg.sales_team.map((m, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input className={input} placeholder="Nama" value={m.name} onChange={(e) => setMember(i, 'name', e.target.value)} aria-label="Nama anggota" />
              <input className={input} placeholder="Email" type="email" value={m.email} onChange={(e) => setMember(i, 'email', e.target.value)} aria-label="Email anggota" />
              <div className="flex gap-2">
                <input className={input} placeholder="No. WhatsApp" inputMode="tel" value={m.phone} onChange={(e) => setMember(i, 'phone', e.target.value)} aria-label="WhatsApp anggota" />
                <button type="button" aria-label="Hapus anggota" onClick={() => setCfg((c) => ({ ...c, sales_team: c.sales_team.filter((_, j) => j !== i) }))}
                  className="w-11 h-11 flex-none rounded-xl border border-red-500/30 bg-red-500/10 text-red-300 grid place-items-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          <Btn onClick={() => setCfg((c) => ({ ...c, sales_team: [...c.sales_team, { name: '', email: '', phone: '' }] }))}><Plus className="w-4 h-4" /> Tambah anggota tim</Btn>
        </div>
        <div className="flex flex-wrap gap-4 mt-3 text-sm font-semibold text-white">
          <label className="inline-flex items-center gap-2 min-h-[44px]"><input type="checkbox" checked={!!cfg.sales_email} onChange={(e) => set('sales_email', e.target.checked)} className="w-5 h-5" style={{ accentColor: 'var(--tv-accent)' }} /> Email</label>
          <label className="inline-flex items-center gap-2 min-h-[44px]"><input type="checkbox" checked={!!cfg.sales_wa} onChange={(e) => set('sales_wa', e.target.checked)} className="w-5 h-5" style={{ accentColor: 'var(--tv-accent)' }} /> WhatsApp</label>
        </div>
        {digest && (
          <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-3">
            <div className="text-sm font-bold text-white mb-2">Isi rekap kalau dikirim sekarang</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {digest.sections.map((s) => (
                <div key={s.key} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-white/80">{s.title}</span><b className="text-white">{s.items.length}</b>
                </div>
              ))}
            </div>
            <div className="mt-3"><Btn testid="run-sales" onClick={() => run('sales')} busy={busy === 'run-sales'} disabled={!data.settings.sales_team.length}><Send className="w-4 h-4" /> Kirim rekap sekarang</Btn></div>
            {!data.settings.sales_team.length && <p className="text-xs text-white/60 mt-2">Tambah anggota tim lalu simpan dulu.</p>}
          </div>
        )}
        <SampleTest testid="sample-sales" label="Tes email rekap tim sales" hint="Kirim contoh rekap hari ini hanya ke email ini, tanpa mengirim ke tim."
          value={testTo.email} onChange={(v) => setTestTo((t) => ({ ...t, email: v }))} onSend={() => test('email', 'sales')} busy={busy === 'test-sales-email'} />
      </Card>

      <Card icon={MessageCircle} title="WhatsApp gateway" sub="Pakai Fonnte (fonnte.com). Isi token API supaya reminder WA ikut terkirim.">
        <div className="text-sm text-white">Status: <b>{data.wa_configured ? `aktif (token ${data.settings.wa_token_hint})` : 'belum aktif, WA dilewati'}</b></div>
        <div className="flex gap-2 mt-2">
          <input className={input} type="password" placeholder={data.wa_configured ? 'Ganti token Fonnte' : 'Tempel token Fonnte'} value={token} onChange={(e) => setToken(e.target.value)} aria-label="Token Fonnte" autoComplete="off" />
          <Btn onClick={() => { save({ wa_token: token }); setToken(''); }} busy={busy === 'save'} disabled={!token}>Simpan</Btn>
        </div>
        {data.wa_configured && <button type="button" onClick={() => save({ wa_token: '' })} className="mt-2 text-xs font-bold text-red-300 underline min-h-[32px]">Hapus token</button>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
          <div className="flex gap-2">
            <input className={input} placeholder="Email untuk tes" type="email" value={testTo.email} onChange={(e) => setTestTo((t) => ({ ...t, email: e.target.value }))} aria-label="Email tes" />
            <Btn onClick={() => test('email')} busy={busy === 'test-ping-email'} disabled={!testTo.email}><Mail className="w-4 h-4" /> Tes</Btn>
          </div>
          <div className="flex gap-2">
            <input className={input} placeholder="No. WA untuk tes" inputMode="tel" value={testTo.wa} onChange={(e) => setTestTo((t) => ({ ...t, wa: e.target.value }))} aria-label="WhatsApp tes" />
            <Btn onClick={() => test('wa')} busy={busy === 'test-ping-wa'} disabled={!testTo.wa}><MessageCircle className="w-4 h-4" /> Tes</Btn>
          </div>
        </div>
      </Card>

      <div ref={barRef} data-testid="automation-savebar" data-stuck={stuck ? '1' : '0'}
        className={`sticky z-20 bottom-[-30px] md:bottom-0 flex justify-end px-4 py-3 md:mx-0 md:rounded-2xl md:border md:border-white/10 md:shadow-none bg-[#160F0F] transition-[border-radius,margin] duration-200 ${stuck ? '-mx-[14px] rounded-t-3xl border-t border-white/10 shadow-[0_-10px_24px_rgba(0,0,0,0.18),0_44px_0_0_var(--tv-surface,#160F0F)]' : 'rounded-2xl border border-white/10'}`}>
        <Btn primary wide testid="automation-save" onClick={() => save()} busy={busy === 'save'}>Simpan pengaturan otomasi</Btn>
      </div>
      <div ref={sentinel} aria-hidden="true" className="h-px" style={{ marginTop: -1 }} />

      <Card icon={RefreshCw} title="Riwayat pengiriman" sub="40 pengiriman terakhir (disimpan 60 hari)."
        right={<button type="button" onClick={load} aria-label="Muat ulang riwayat" className="w-10 h-10 rounded-xl border border-white/10 bg-white/5 grid place-items-center text-white"><RefreshCw className="w-4 h-4" /></button>}>
        {(data.log || []).length === 0 ? <p className="text-sm text-white/60">Belum ada pengiriman.</p> : (
          <ul className="divide-y divide-white/5">
            {data.log.map((l, i) => (
              <li key={i} className="py-2.5 flex items-start gap-2 text-sm">
                {l.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-300 flex-none" /> : <XCircle className="w-4 h-4 mt-0.5 text-red-300 flex-none" />}
                <span className="flex-1 min-w-0 text-white">
                  <b>{{ tenant: 'Tenant', sales: 'Tim sales', test: 'Tes' }[l.kind] || l.kind}</b> · {l.target}
                  <span className="block text-xs text-white/60">{fmt(l.at)} · {(l.channels || []).join(' + ') || 'tidak terkirim'}{l.detail ? ` · ${l.detail}` : ''}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
